/**
 * §1/A.6 OTF1 FLAC 인코더 래퍼 — WebCodecs `AudioEncoder` 를 감싸 PCM16 프레임을
 * EncodedAudioChunk 로 바꾼다. 실패하면 세션 전체를 PCM 으로 영구 강등한다
 * (조용한 유실 금지 — 실패한 프레임도 반드시 레거시 경로로 나가야 한다).
 *
 * O3(컨테이너 형식) 은 이 환경에서 실측하지 못했다 — 아래 stripFlacContainer
 * 참고. Chromium 이 없는 환경에서도 동작해야 하므로 순수 함수로 분리해 둔다.
 */

/**
 * 이 브라우저가 FLAC WebCodecs 인코딩을 지원하는가. 절대 던지지 않는다 —
 * `AudioEncoder` 자체가 없거나 호출이 거부되면 false 로 접는다(호출부가
 * 이 값 하나로 PCM/OTF1 을 가른다, capture.ts).
 */
export async function isFlacSupported(): Promise<boolean> {
  if (typeof AudioEncoder === 'undefined') return false
  try {
    const support = await AudioEncoder.isConfigSupported({
      codec: 'flac',
      sampleRate: 16000,
      numberOfChannels: 1,
    })
    return support.supported === true
  } catch {
    return false
  }
}

/**
 * ── O3: EncodedAudioChunk 가 bare FLAC 프레임(0xFFF8 시작)인지, 'fLaC'
 * 컨테이너(스트림 헤더+STREAMINFO)를 두르고 나오는지 ──────────────────────
 *
 * 이 저장소 환경에는 Chromium 이 있으나(playwright chromium-1234) **headless
 * 빌드에 WebCodecs 전역(AudioEncoder/VideoEncoder)이 아예 없다**(실측: 둘 다
 * `typeof === 'undefined'`, about:blank 컨텍스트). 헤드풀 모드는 이 환경에
 * X 서버가 없어 실행 불가(`Missing X server or $DISPLAY`). 그래서 실제
 * Chromium 산출물의 첫 16바이트 hex dump 를 못 얻었다 — 실 브라우저에서
 * 재현·보고가 필요한 항목으로 남긴다(X1 이 담당).
 *
 * 그래서 컨테이너 여부를 **가정하지 않는다.** 순수 함수로 분리해 두고,
 * 실제로 'fLaC' 로 시작하는 청크를 만났을 때만 벗겨서 쓴다 — bare 프레임이면
 * 이 함수는 그대로 통과시킨다.
 */
export function stripFlacContainer(bytes: Uint8Array): Uint8Array {
  // 'fLaC' 매직 + 최소 STREAMINFO 메타데이터 블록(4B 헤더 + 34B) = 42B
  const MAGIC = [0x66, 0x4c, 0x61, 0x43] // 'fLaC'
  const isContainer =
    bytes.byteLength >= 4 && MAGIC.every((b, i) => bytes[i] === b)
  if (!isContainer) return bytes

  // 메타데이터 블록을 순회해 '마지막' 블록 다음(=첫 프레임 시작)을 찾는다.
  // 블록 헤더: [0] bit7=last, bits6-0=type, [1:4] 24bit BE 길이.
  let offset = 4
  for (;;) {
    if (offset + 4 > bytes.byteLength) return bytes // 손상 — 벗기지 못한다
    const first = bytes[offset] as number
    const last = (first & 0x80) !== 0
    const len =
      (((bytes[offset + 1] as number) << 16) |
        ((bytes[offset + 2] as number) << 8) |
        (bytes[offset + 3] as number)) >>>
      0
    offset += 4 + len
    if (last) break
  }
  return bytes.subarray(offset)
}

export interface FlacFrame {
  seq: number
  bytes: Uint8Array
}

export interface FlacEncoderOptions {
  /**
   * 인코더가 영구 강등됐다 — 이후 프레임은 전부 PCM 으로 보내야 한다.
   * `orphanedSeqs` 는 이미 encode() 에 넣었지만 청크를 돌려받지 못한 채
   * 강등된 seq 목록이다(비동기 인코더 콜백이 나중에 에러를 낸 경우) —
   * 호출부가 **이 프레임들도** PCM 으로 다시 보내야 조용한 유실이 안 된다.
   */
  onDowngrade: (reason: string, orphanedSeqs: number[]) => void
}

/**
 * PCM16 프레임을 순서대로 넣으면 인코딩된 FLAC 프레임을 콜백으로 돌려주는
 * 래퍼. `encode()` 는 **비동기**다 — 한 프레임을 제출하고 `encoder.flush()`
 * 가 풀릴 때까지 그 사이 도착한 청크를 전부 이 프레임 것으로 모은다(0개,
 * 1개, N개 전부 정상 — 청크 개수 자체는 더 이상 오류 조건이 아니다). 0개면
 * 이 프레임은 PCM 으로 대신 보내야 한다는 신호를 `onChunk` 로 낸다(세션
 * 전체 강등이 아니라 **이 프레임 하나만**의 폴백). 강등은 인코더 자체가
 * 죽었을 때만(초기화 실패·error 콜백) 일어난다.
 *
 * 방출 순서는 seq 오름차순을 지킨다 — `encode()` 를 호출부가 순서대로
 * await 하지 않고 겹쳐 부르더라도(파이프라이닝), 늦게 끝난 이전 seq 가
 * 먼저 끝난 다음 seq 보다 늦게 콜백에 나가면 안 된다. seq 키 방출 큐로
 * "frame n-1 방출/강등 전에는 frame n 을 내보내지 않는다"를 강제한다.
 */
export class FlacEncoderSession {
  private encoder: AudioEncoder | null = null
  private downgraded = false
  private readonly opts: FlacEncoderOptions
  private onChunk: ((frame: FlacFrame | { seq: number; bytes: null }) => void) | null = null
  /**
   * F-A — "다음 방출 seq = nextEmitSeq" 라는 +1 가정을 버렸다. capture.ts 가
   * `!sink.ready` 일 때 세션에 아예 들어오지 않고 PCM 으로 우회하는 seq 가
   * 있어(encodeAndEmit 참고), 이 세션은 **자기가 실제로 받은 seq 집합만**
   * 안다. 그래서 방출 게이트를 "대기 집합에서 가장 작은 seq" 로 둔다 —
   * 세션에 들어오는 seq 는 항상 단조 증가(Framer 가 발급)이므로, 지금 대기
   * 중인 것 중 최소값보다 작은 seq 가 나중에 들어올 수 없다. 그 최소값이
   * 곧 "다음에 낼 차례" 다. 우회된 seq 는애초에 여기 들어오지 않으니
   * 정체가 생기지 않는다.
   */
  private readonly pendingEmit = new Map<number, FlacFrame | { seq: number; bytes: null }>()

  constructor(opts: FlacEncoderOptions) {
    this.opts = opts
  }

  get isDowngraded(): boolean {
    return this.downgraded
  }

  /**
   * 인코더를 연다. 실패하면(구성 미지원·초기화 예외) 즉시 강등 상태로
   * 시작한다 — 호출부는 encode() 를 부르지 않고 처음부터 PCM 을 쓰면 된다.
   */
  init(onChunk: (frame: FlacFrame | { seq: number; bytes: null }) => void): void {
    this.onChunk = onChunk
    if (typeof AudioEncoder === 'undefined') {
      this.downgrade('AudioEncoder 미지원')
      return
    }
    try {
      this.encoder = new AudioEncoder({
        output: (chunk) => this.handleChunk(chunk),
        error: () => this.downgrade('encoder error 콜백'),
      })
      this.encoder.configure({ codec: 'flac', sampleRate: 16000, numberOfChannels: 1 })
    } catch {
      this.downgrade('encoder 초기화 실패')
    }
  }

  /**
   * PCM16 샘플 한 프레임을 인코더에 넣고, 이 프레임에 속한 청크를 전부
   * 모을 때까지(=encoder.flush() 가 풀릴 때까지) 기다린다. 이미 강등된
   * 세션에서는 아무 일도 하지 않는다(호출부가 강등 이후 이 메서드를 부르지
   * 않는 것이 정상이지만, 방어적으로 no-op).
   *
   * 청크 0개(flush 까지 output 콜백이 한 번도 안 옴)는 에러가 아니다 — 이
   * 프레임은 PCM 폴백 신호(`{seq, bytes: null}`)로 onChunk 에 나간다.
   * 청크 여러 개는 seq 순서대로 이어붙이고, `stripFlacContainer` 는 **첫
   * 청크에만** 적용한다(컨테이너 헤더는 스트림당 한 번만 나온다).
   *
   * WebCodecs output 콜백에는 청크가 어느 encode() 호출에 속하는지 알려줄
   * 식별자가 없다 — 그래서 호출부(capture.ts)가 encode() 를 순서대로
   * await 하지 않고 겹쳐 부르더라도, 이 메서드는 내부에서 **직렬 큐**로
   * 실제 인코더 호출(encode+flush)을 하나씩만 진행한다(runQueue). 그
   * 위에서 seq 키 방출 큐(release/pendingEmit)가 콜백 순서를 보장한다.
   */
  async encode(seq: number, samples: Int16Array, sampleRate: number): Promise<void> {
    if (this.downgraded || !this.encoder) return
    // F-B1 — 이 seq 는 직렬 큐에 태워진 순간부터 추적한다(아직 encodeOne 이
    // 시작되지 않아 inFlightSeq 에 없는 동안에도 강등 시 orphaned 로 잡혀야
    // 한다 — 안 그러면 이 seq 는 호출부 inFlightPcm 에 원본이 남은 채
    // 아무도 내보내지 않는다).
    this.queuedSeq.add(seq)

    const task = (): Promise<void> => this.encodeOne(seq, samples, sampleRate)
    const run = this.queue.then(task, task)
    // 큐 자체는 실패해도 끊기면 안 된다 — 다음 프레임이 계속 진행돼야 한다.
    this.queue = run.catch(() => undefined)
    return run
  }

  /** 직렬화용 큐 — encode() 호출들이 순서 없이 겹쳐 들어와도 인코더 호출은 하나씩. */
  private queue: Promise<void> = Promise.resolve()
  /** F-B1 — encode() 에 태워졌지만 아직 encodeOne() 이 시작 안 된 seq. */
  private readonly queuedSeq = new Set<number>()

  private async encodeOne(seq: number, samples: Int16Array, sampleRate: number): Promise<void> {
    this.queuedSeq.delete(seq)
    if (this.downgraded || !this.encoder) return
    this.inFlightSeq.add(seq)
    const chunks: Uint8Array[] = []
    this.activeCollector = (chunk) => {
      const buf = new ArrayBuffer(chunk.byteLength)
      chunk.copyTo(buf)
      chunks.push(new Uint8Array(buf))
    }

    try {
      const audioData = new AudioData({
        format: 's16',
        sampleRate,
        numberOfFrames: samples.length,
        numberOfChannels: 1,
        timestamp: 0,
        // Int16Array 의 buffer 타입은 ArrayBufferLike(SharedArrayBuffer 포함
        // 가능) 인데 AudioDataInit.data 는 BufferSource 를 요구한다. 캡처
        // 경로가 SharedArrayBuffer 를 쓰지 않으므로 안전한 캐스트다.
        data: samples as unknown as BufferSource,
      })
      this.encoder.encode(audioData)
      audioData.close()
      await this.encoder.flush()
    } catch {
      this.activeCollector = null
      this.downgrade('encode()/flush() 예외')
      this.inFlightSeq.delete(seq)
      return
    }
    this.activeCollector = null
    this.inFlightSeq.delete(seq)
    if (this.downgraded) return // flush 도중 error 콜백으로 강등됐다 — orphanedSeqs 가 처리

    if (chunks.length === 0) {
      this.release(seq, { seq, bytes: null })
      return
    }
    const first = stripFlacContainer(chunks[0] as Uint8Array)
    let total = first.byteLength
    for (let i = 1; i < chunks.length; i += 1) total += (chunks[i] as Uint8Array).byteLength
    const merged = new Uint8Array(total)
    merged.set(first, 0)
    let offset = first.byteLength
    for (let i = 1; i < chunks.length; i += 1) {
      const c = chunks[i] as Uint8Array
      merged.set(c, offset)
      offset += c.byteLength
    }
    this.release(seq, { seq, bytes: merged })
  }

  close(): void {
    try {
      this.encoder?.close()
    } catch {
      /* 이미 닫혔거나 에러 상태 — 무시 */
    }
    this.encoder = null
  }

  /** 지금 진행 중인 encode() 호출의 청크 수집기 — 직렬 큐 덕에 한 번에 하나뿐이다. */
  private activeCollector: ((chunk: EncodedAudioChunk) => void) | null = null
  /** encode() 에 들어갔지만 아직 끝나지 않은 seq — 강등 시 orphanedSeqs 계산용. */
  private readonly inFlightSeq = new Set<number>()

  private handleChunk(chunk: EncodedAudioChunk): void {
    if (this.downgraded) return
    // encode() 가 내부 큐로 직렬화되므로 이 시점에 활성 수집기는 항상 하나뿐이다.
    this.activeCollector?.(chunk)
  }

  /**
   * seq 오름차순으로만 onChunk 를 호출한다 — 늦게 끝난 이전 seq 를 기다린다.
   *
   * F-A: 다음에 낼 차례는 "지금 대기 중인 것 중 가장 작은 seq" 다. 아직
   * encode() 가 안 끝난(=inFlightSeq 에 남아 있는) seq 중 이 프레임보다
   * 작은 것이 있으면 그것을 먼저 기다려야 하므로 방출하지 않는다 — 그
   * 경우는 pendingEmit 의 최소값 검사만으로는 못 잡으므로, inFlightSeq 도
   * 함께 본다(아직 청크로 안 돌아온 더 이른 seq가 있으면 정지).
   */
  private release(seq: number, frame: FlacFrame | { seq: number; bytes: null }): void {
    this.pendingEmit.set(seq, frame)
    for (;;) {
      if (this.pendingEmit.size === 0) break
      const minPending = Math.min(...this.pendingEmit.keys())
      // 아직 진행 중인 더 이른 seq 가 있으면 그것을 기다린다(순서 보존).
      const hasEarlierInFlight = [...this.inFlightSeq].some((s) => s < minPending)
      if (hasEarlierInFlight) break
      const ready = this.pendingEmit.get(minPending) as FlacFrame | { seq: number; bytes: null }
      this.pendingEmit.delete(minPending)
      this.onChunk?.(ready)
    }
  }

  private downgrade(reason: string): void {
    if (this.downgraded) return
    this.downgraded = true
    // 강등 이후 아직 방출 큐를 못 나간 seq 는 전부 호출부가 PCM 으로 다시
    // 보내야 한다 — 여기서 버리지 않고 orphanedSeqs 로 그대로 넘긴다(조용한
    // 유실 금지). F-B1: 큐에 태워졌지만 아직 시작 안 한 seq(queuedSeq),
    // encode() 진행 중인 seq(inFlightSeq), 방출 대기 중인 seq(pendingEmit)
    // 셋의 합집합이어야 한다 — 하나라도 빠지면 그 seq 의 원본 PCM 을 아무도
    // 내보내지 않는다.
    // DEF-099: **seq 오름차순**이어야 한다. 호출부(capture.ts)는 이 배열
    // 순서 그대로 emit 하고, 서버(audio.py)는 seq 역행 프레임을 버린다 —
    // 집합 나열 순서(queued→inFlight→pending)로 내보내면 앞 프레임이
    // 조용히 유실된다(gaps=0 으로 보여 갭 카드도 안 뜬다).
    const orphaned = [
      ...new Set([...this.queuedSeq, ...this.inFlightSeq, ...this.pendingEmit.keys()]),
    ].sort((a, b) => a - b)
    this.activeCollector = null
    this.queuedSeq.clear()
    this.inFlightSeq.clear()
    this.pendingEmit.clear()
    this.close()
    // 원샷 콘솔 마커 — 반복 스팸 없이 딱 한 번만.
    // eslint-disable-next-line no-console
    console.warn(`[flac-encoder] PCM 으로 영구 강등: ${reason}`)
    this.opts.onDowngrade(reason, orphaned)
  }
}

/**
 * 기동 시 자문용 왕복 점검 — 무음 1프레임을 인코드→디코드해 값이 돌아오는지
 * 확인한다. **로그만 남기고 세션 시작을 절대 막지 않는다**(SPEC C2) —
 * 이 점검이 실패해도 encode() 경로에서 다시 실패하면 그때 강등되므로
 * 이중 방어일 뿐, 게이트가 아니다.
 */
export async function advisoryRoundTripCheck(): Promise<void> {
  if (typeof AudioEncoder === 'undefined' || typeof AudioDecoder === 'undefined') return
  try {
    const nsamp = 5120
    const silence = new Int16Array(nsamp)
    const chunks: EncodedAudioChunk[] = []
    const encoder = new AudioEncoder({
      output: (chunk) => chunks.push(chunk),
      error: () => {
        /* 자문용 — 여기서 세션을 막지 않는다 */
      },
    })
    encoder.configure({ codec: 'flac', sampleRate: 16000, numberOfChannels: 1 })
    const audioData = new AudioData({
      format: 's16',
      sampleRate: 16000,
      numberOfFrames: nsamp,
      numberOfChannels: 1,
      timestamp: 0,
      data: silence,
    })
    encoder.encode(audioData)
    audioData.close()
    await encoder.flush()
    encoder.close()

    if (chunks.length === 0) {
      console.warn('[flac-encoder] 자문 왕복 점검: 인코더가 청크를 내지 않았다')
      return
    }
    let decodedOk = false
    const decoder = new AudioDecoder({
      output: () => {
        decodedOk = true
      },
      error: () => {
        /* 자문용 */
      },
    })
    decoder.configure({ codec: 'flac', sampleRate: 16000, numberOfChannels: 1 })
    for (const c of chunks) decoder.decode(c)
    await decoder.flush()
    decoder.close()
    if (!decodedOk) console.warn('[flac-encoder] 자문 왕복 점검: 디코드 결과 없음')
  } catch (e) {
    console.warn('[flac-encoder] 자문 왕복 점검 실패(세션 시작은 막지 않음):', e)
  }
}
